import { useEffect, useRef, useState } from "react";
import { useStore, type FolderSummary } from "@/lib/store";
import { inTauri } from "@/lib/tools";

async function openSandboxFolder(chatId: string) {
  const { invoke } = await import("@tauri-apps/api/core");
  const path = await invoke<string>("sandbox_home", { chatId });
  await invoke("open_in_finder", { path });
}

type Props = {
  title: string;
  hasActiveChat: boolean;
  currentFolderId: string | null;
  pinned: boolean;
  folders: FolderSummary[];
  onNew: () => void;
  onRename: () => void;
  onSuggestTitle: () => void;
  onTogglePin: (pinned: boolean) => void;
  onMove: (folderId: string | null) => void;
  onDelete: () => void;
  onContinueInNew: () => void;
  continueBusy?: boolean;
  suggestBusy?: boolean;
};

export function ChatTitleMenu({ title, hasActiveChat, currentFolderId, pinned, folders, onNew, onRename, onSuggestTitle, onTogglePin, onMove, onDelete, onContinueInNew, continueBusy, suggestBusy }: Props) {
  const [open, setOpen] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const activeId = useStore((s) => s.activeId);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowMove(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); setShowMove(false); }
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => { setOpen(false); setShowMove(false); };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-sm font-semibold leading-tight min-w-0 text-left hover:text-primary transition-colors"
        title={title}
      >
        <span className="truncate max-w-[40vw] sm:max-w-[20em]">{title}</span>
        <i className="fa-solid fa-caret-down text-xs opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border bg-popover py-1 text-sm shadow-md">
          <button
            onClick={() => { close(); onNew(); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
          >
            <i className="fa-solid fa-plus w-4 text-center text-xs" />
            New chat
          </button>
          {hasActiveChat && (
            <>
              <div className="my-1 border-t" />
              <button
                onClick={() => { close(); onRename(); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
              >
                <i className="fa-solid fa-pen w-4 text-center text-xs" />
                Rename
              </button>
              <button
                onClick={() => { onSuggestTitle(); }}
                disabled={suggestBusy}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted disabled:opacity-50"
              >
                <i className={`fa-solid w-4 text-center text-xs ${suggestBusy ? "fa-spinner-third fa-spin" : "fa-wand-magic-sparkles"}`} />
                Suggest title
              </button>
              <button
                onClick={() => { close(); onTogglePin(!pinned); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
              >
                <i className={`w-4 text-center text-xs ${pinned ? "fa-solid fa-thumbtack text-primary" : "fa-solid fa-thumbtack"}`} />
                {pinned ? "Unpin chat" : "Pin chat"}
              </button>
              <button
                onClick={() => { close(); onContinueInNew(); }}
                disabled={continueBusy}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted disabled:opacity-50"
              >
                <i className={`fa-solid w-4 text-center text-xs ${continueBusy ? "fa-spinner-third fa-spin" : "fa-arrow-right-from-bracket"}`} />
                Continue in new chat
              </button>
              {inTauri && activeId && (
                <button
                  onClick={() => { close(); openSandboxFolder(activeId).catch((e) => console.warn("Couldn't open the sandbox folder:", e)); }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
                >
                  <i className="fa-solid fa-box-open w-4 text-center text-xs" />
                  Open sandbox folder
                </button>
              )}
              <button
                onClick={() => setShowMove((s) => !s)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
              >
                <i className="fa-solid fa-folder-arrow-up w-4 text-center text-xs" />
                Move to folder
                <i className={`fa-solid fa-chevron-${showMove ? "down" : "right"} ml-auto text-[10px] opacity-60`} />
              </button>
              {showMove && (
                <div className="border-y bg-muted/30 py-1">
                  <button
                    onClick={() => { close(); onMove(null); }}
                    className={`flex w-full items-center gap-2 px-5 py-1.5 text-left hover:bg-muted ${currentFolderId == null ? "font-medium text-primary" : ""}`}
                  >
                    <i className="fa-solid fa-inbox w-4 text-center text-xs" />
                    Unfiled
                  </button>
                  {folders.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => { close(); onMove(f.id); }}
                      className={`flex w-full items-center gap-2 px-5 py-1.5 text-left hover:bg-muted truncate ${currentFolderId === f.id ? "font-medium text-primary" : ""}`}
                    >
                      <i className="fa-solid fa-folder w-4 text-center text-xs" />
                      <span className="truncate">{f.name}</span>
                    </button>
                  ))}
                  {folders.length === 0 && (
                    <p className="px-5 py-1.5 text-xs text-muted-foreground">No folders yet</p>
                  )}
                </div>
              )}
              <div className="my-1 border-t" />
              <button
                onClick={() => { close(); onDelete(); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-destructive hover:bg-destructive/10"
              >
                <i className="fa-solid fa-trash-can w-4 text-center text-xs" />
                Delete chat
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
